import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { fetcher } from 'src/api/fetcher';
import { API_RESOURCES } from 'src/utils/constants';

export const useMlineLPSByPrinterUri = printerUri => {
  return useQuery({
    queryKey: ['mlineLPSByPrinterUri', printerUri],
    queryFn: () =>
      fetcher(API_RESOURCES.MLINE_LASER_PROCESSING_SYSTEM, {
        params: {
          'filter[printer]': printerUri,
        },
      }),
    enabled: !!printerUri,
    select: data => data.resources?.[0],
    refetchOnWindowFocus: false,
  });
};

export const useBatchActionTransactionByModuleBatchUri = (
  moduleBatchUri,
  key = 'byModuleBatchUri'
) => {
  return useQuery({
    queryKey: ['moduleBatchUri', key, moduleBatchUri],
    queryFn: () =>
      fetcher(API_RESOURCES.MATERIAL_BATCH_ACTION_TRANSACTION, {
        params: {
          'filter[material_batch]': moduleBatchUri,
        },
      }),
    enabled: !!moduleBatchUri,
    select: data => data.resources,
  });
};

/**
 * Fetch ALL transactions without any additional filters.
 * This query is used as a “source of truth” for calculated fields.
 */
export const useAllTransactions = parentTransactionUri => {
  return useQuery({
    queryKey: ['batchTransactions', parentTransactionUri, 'all'],
    queryFn: async () => {
      const params = {
        'filter[parent_transaction]': parentTransactionUri,
      };
      const response = await fetcher(API_RESOURCES.MATERIAL_BATCH_ACTION_TRANSACTION, { params });
      return response.resources;
    },
    enabled: !!parentTransactionUri,
  });
};

/**
 * Fetch transactions based on additionalFilters.
 * This hook creates a separate cache entry for each filter combination.
 */
export const useFilteredTransactions = (parentTransactionUri, additionalFilters = {}) => {
  return useQuery({
    queryKey: ['batchTransactions', parentTransactionUri, additionalFilters],
    queryFn: async () => {
      const params = {
        'filter[parent_transaction]': parentTransactionUri,
        ...(additionalFilters.isComplete !== undefined && {
          'filter[is_complete]': additionalFilters.isComplete,
        }),
        ...(additionalFilters.containerType && {
          'filter[container_type]': additionalFilters.containerType,
        }),
      };
      const response = await fetcher(API_RESOURCES.MATERIAL_BATCH_ACTION_TRANSACTION, { params });
      return response.resources;
    },
    enabled: !!parentTransactionUri,
  });
};

/**
 * Merges filtered transaction data with full dataset when necessary.
 *
 * This hook handles two scenarios:
 * 1. When using the "All" filter: Falls back to cached data for calculated fields
 *    if filtered results are missing required calculated fields
 * 2. When using specific filters: Uses strictly filtered results with no merging
 *
 * @param {string} parentTransactionUri - The URI for the parent transaction
 * @param {Object} additionalFilters - Optional filters like isComplete or containerType
 * @returns {Object} - The data object and query information
 */
export const useMergedTransactions = (parentTransactionUri, additionalFilters = {}) => {
  const queryClient = useQueryClient();

  // Get filtered data based on provided filters
  const { data: filteredData = [], ...queryInfo } = useFilteredTransactions(
    parentTransactionUri,
    additionalFilters
  );

  // Retrieve the complete dataset from cache
  const cachedFullData =
    queryClient.getQueryData(['batchTransactions', parentTransactionUri, 'all']) || [];

  // Determine if we're using the "All" filter (no specific filters applied)
  const isUsingAllFilter = !additionalFilters.isComplete && !additionalFilters.containerType;

  // Check if filtered data has the necessary calculated fields
  const hasRequiredCalculatedFields =
    filteredData.length > 0 && filteredData[0]?.initial_quantity_available !== undefined;

  // Select the appropriate data source based on filters and data completeness
  let resultData;

  if (isUsingAllFilter) {
    // When using "All" filter, choose based on data completeness
    resultData = hasRequiredCalculatedFields ? filteredData : cachedFullData;
  } else {
    // When using specific filters, always use filtered results
    resultData = filteredData;
  }

  return {
    data: resultData,
    ...queryInfo,
  };
};
